import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { buildPrescriptionBatchJobs } from './prescriptionBatchCreate';
import { normalizePrescriptionType, resolveEffectivePrescriptionType } from './prescriptionTypes';
import { itemsHaveContinuousUse } from './prescriptionContinuousUse';

/** Campos que as regras de tipo/via leem; o resto do item segue livre. */
type PrescriptionItemPayload = Record<string, unknown> & {
  is_controlled?: boolean;
  is_antimicrobial?: boolean;
  is_continuous_use?: boolean | null;
};

export type PrescriptionSubmitParams = {
  editingId?: string | null;
  /** Data de emissão ao abrir a edição — define qual job atualiza o registro existente. */
  editingOriginalIssueDate?: string | null;
  prescriptionForm: Record<string, unknown>;
  format: 'structured' | 'free_text';
  bodyText: string;
  items: Record<string, unknown>[];
  splitMedications: boolean;
  scheduledDates: string[];
  /** Datas programadas só para controlados/antimicrobianos (demais itens ficam na 1ª data). */
  scheduleSpecialItemsOnly?: boolean;
  patientId: string;
  professionalId: string | null;
  printSettings?: Record<string, unknown>;
  stripUiFields: (item: Record<string, unknown>) => Record<string, unknown>;
};

async function persistPrescriptionPayload(
  method: 'post' | 'put',
  path: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const res = method === 'put'
    ? await axios.put(getApiUrl(path), payload)
    : await axios.post(getApiUrl(path), payload);
  let saved = res.data?.data ?? res.data;
  const newId = saved?.id;
  if (newId && !saved?.items?.length && Array.isArray(payload.items) && payload.items.length) {
    try {
      const full = await axios.get(getApiUrl(`/clinic/prescriptions/${newId}`));
      saved = full.data?.data ?? saved;
    } catch {
      /* keep partial */
    }
  }
  return saved;
}

function buildBasePayload(params: PrescriptionSubmitParams) {
  const {
    prescriptionForm,
    format,
    bodyText,
    items,
    patientId,
    professionalId,
    printSettings,
  } = params;

  return {
    ...prescriptionForm,
    is_continuous_use: format === 'structured' ? itemsHaveContinuousUse(items) : false,
    format,
    body_text: format === 'free_text' ? bodyText : null,
    patient_id: patientId,
    professional_id: professionalId,
    ...(printSettings ? { print_settings: printSettings } : {}),
  };
}

function payloadForJob(
  basePayload: Record<string, unknown>,
  job: { issue_date: string; items: Record<string, unknown>[] },
  format: 'structured' | 'free_text',
  bodyText: string,
  emitBatchId: string | null,
  stripUiFields: PrescriptionSubmitParams['stripUiFields'],
) {
  const jobItems: PrescriptionItemPayload[] = format === 'structured'
    ? job.items.map((item, idx) => ({ ...stripUiFields(item), sort_order: idx }))
    : [];

  return {
    ...basePayload,
    issue_date: job.issue_date,
    prescription_type: normalizePrescriptionType(
      resolveEffectivePrescriptionType(String(basePayload.prescription_type || 'simple'), jobItems),
    ),
    is_continuous_use: format === 'structured' ? itemsHaveContinuousUse(jobItems) : false,
    items: jobItems,
    ...(format === 'free_text' ? { body_text: bodyText } : {}),
    ...(emitBatchId
      ? { metadata: { emit_batch_id: emitBatchId, emit_scheduled_date: job.issue_date } }
      : {}),
  };
}

export async function submitPrescriptionForms(params: PrescriptionSubmitParams): Promise<any[]> {
  const {
    editingId,
    editingOriginalIssueDate,
    prescriptionForm,
    format,
    bodyText,
    items,
    splitMedications,
    scheduledDates,
    scheduleSpecialItemsOnly,
    stripUiFields,
  } = params;

  const structuredItems: PrescriptionItemPayload[] = format === 'structured'
    ? items
      .filter((i) => String(i.medication_name || '').trim())
      .map((item, idx) => ({ ...stripUiFields(item), sort_order: idx }))
    : [];

  const basePayload = buildBasePayload(params);

  const jobs = buildPrescriptionBatchJobs({
    format,
    items: structuredItems,
    splitMedications,
    scheduledDates,
    scheduleSpecialItemsOnly,
  });

  if (editingId) {
    if (!jobs.length) return [];

    const originalDate = String(editingOriginalIssueDate || prescriptionForm.issue_date || jobs[0].issue_date || '').slice(0, 10);
    let putIndex = jobs.findIndex((j) => j.issue_date === originalDate);
    if (putIndex < 0) putIndex = 0;

    const emitBatchId = jobs.length > 1 ? crypto.randomUUID() : null;
    const savedList: any[] = [];

    for (let i = 0; i < jobs.length; i++) {
      const payload = payloadForJob(basePayload, jobs[i], format, bodyText, emitBatchId, stripUiFields);
      if (i === putIndex) {
        savedList.push(await persistPrescriptionPayload('put', `/clinic/prescriptions/${editingId}`, payload));
      } else {
        savedList.push(await persistPrescriptionPayload('post', '/clinic/prescriptions', payload));
      }
    }
    return savedList;
  }

  if (!jobs.length) return [];

  const emitBatchId = jobs.length > 1 ? crypto.randomUUID() : null;
  const savedList: any[] = [];

  for (const job of jobs) {
    const payload = payloadForJob(basePayload, job, format, bodyText, emitBatchId, stripUiFields);
    savedList.push(await persistPrescriptionPayload('post', '/clinic/prescriptions', payload));
  }

  return savedList;
}
